import React, { useState, useRef } from 'react';
import { Upload } from 'lucide-react';

export default function DragDropZone({ 
  onFileSelect, 
  accept = '*', 
  disabled = false,
  children,
  className = '',
  multiple = false,
  icon: Icon = Upload,
  message = 'Drag and drop files here, or click to browse',
  isMobile = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isMobile) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isMobile) return;

    const files = Array.from(e.dataTransfer.files);
    
    const acceptedFiles = accept === '*' ? files : files.filter(file => {
      const acceptTypes = accept.split(',').map(type => type.trim());
      return acceptTypes.some(type => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        if (type.endsWith('/*')) {
          const mainType = type.split('/')[0];
          return file.type.startsWith(mainType);
        }
        return file.type === type;
      });
    });

    if (acceptedFiles.length > 0) {
      if (multiple) {
        onFileSelect(acceptedFiles);
      } else {
        onFileSelect(acceptedFiles[0]);
      }
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      if (multiple) {
        onFileSelect(Array.from(e.target.files));
      } else {
        onFileSelect(e.target.files[0]);
      }
    }
  };

  return (
    <div
      onDragEnter={!isMobile ? handleDragEnter : undefined}
      onDragLeave={!isMobile ? handleDragLeave : undefined}
      onDragOver={!isMobile ? handleDragOver : undefined}
      onDrop={!isMobile ? handleDrop : undefined}
      onClick={handleClick}
      className={`
        border-2 border-dashed p-4 text-center cursor-pointer transition-all rounded-pill
        ${isDragging && !isMobile
          ? 'border-brand bg-brand/10 scale-[1.02]' 
          : 'border-surface-border-light hover:border-brand'
        }
        ${disabled ? 'opacity-disabled cursor-not-allowed' : ''}
        ${className}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        disabled={disabled}
        multiple={multiple}
        className="hidden"
      />
      
      {children || (
        <>
          <Icon className={`w-5 h-5 mx-auto mb-2 transition-transform ${isDragging && !isMobile ? 'scale-125 text-brand' : 'text-white'}`} />
          <p className={`text-sm transition-colors ${isDragging && !isMobile ? 'text-brand' : 'text-text-muted'}`}>
            {isDragging && !isMobile ? 'Drop files here' : (isMobile ? message.replace('Drag and drop files here, or click', 'Click').replace('Drag and drop', 'Click') : message)}
          </p>
        </>
      )}
    </div>
  );
}
